import { atom } from 'jotai'

import { OrderFormSummaryTab } from '@/state/order'

// Order types that have a first-use guide in the order form. To add one: map its tab
// below, add `orderTypeGuide.<key>` copy to every messages/*.json, and drop
// `<key>-buy.json` / `<key>-sell.json` into public/lottie/order-form/{dark,light}.
export const ORDER_TYPE_GUIDE_BY_TAB = {
  [OrderFormSummaryTab.Limit]: 'limit',
  [OrderFormSummaryTab.Market]: 'market',
  [OrderFormSummaryTab.TWAP]: 'twap',
  [OrderFormSummaryTab.LimitChase]: 'limit-chase',
  [OrderFormSummaryTab.AdvancedTwap]: 'advanced-twap',
  [OrderFormSummaryTab.Scale]: 'scale',
  [OrderFormSummaryTab.RFQ]: 'rfq',
} as const satisfies Partial<Record<OrderFormSummaryTab, string>>

// Not tabs: spreader is a mode over the legs, and an armed trigger can ride most tabs.
const SPREADER_GUIDE_KEY = 'multi-leg-spreader'
const TRIGGER_GUIDE_KEY = 'trigger-order'

export type OrderTypeGuideKey =
  | (typeof ORDER_TYPE_GUIDE_BY_TAB)[keyof typeof ORDER_TYPE_GUIDE_BY_TAB]
  | typeof SPREADER_GUIDE_KEY
  | typeof TRIGGER_GUIDE_KEY

/**
 * The guide for what submit will actually place, in submitOrder's order
 * (use-unified-order-form.ts): spreader ignores the tab, scale can't carry a trigger, and
 * otherwise an armed trigger replaces the tab's guide (Single Hedge only has that one).
 */
export function resolveOrderTypeGuideKey({
  tab,
  isSpreaderMode,
  hasTriggerCondition,
}: {
  tab: OrderFormSummaryTab
  isSpreaderMode: boolean
  hasTriggerCondition: boolean
}): OrderTypeGuideKey | undefined {
  if (isSpreaderMode) return SPREADER_GUIDE_KEY
  if (tab === OrderFormSummaryTab.Scale) return ORDER_TYPE_GUIDE_BY_TAB[tab]
  if (hasTriggerCondition) return TRIGGER_GUIDE_KEY
  return (ORDER_TYPE_GUIDE_BY_TAB as Partial<Record<OrderFormSummaryTab, OrderTypeGuideKey>>)[tab]
}

export type OrderTypeGuideSeen = Partial<Record<OrderTypeGuideKey, boolean>>

/** Stored in the data-manager widget config under this key: `{ [userId]: OrderTypeGuideSeen }`. */
export const ORDER_TYPE_GUIDE_CONFIG_KEY = 'orderTypeGuide'

/** userId -> guides that user has dismissed. Seeded by DataManagerSync from the server. */
export const orderTypeGuideSeenAtom = atom<Record<string, OrderTypeGuideSeen>>({})
